import { Injectable } from "@angular/core";
import { Feature } from "geojson";
import { last } from "lodash-es";
import { combineLatest, filter, map, Observable, startWith } from "rxjs";
import { LinkingService } from "~app/linking/linking.service";
import { IRegoFeatureDefinition } from "~app/linking/mapping";
import { MappingService } from "~app/linking/mapping.service";
import { TagPair, ValmetDnaTagConfig } from "~app/shared/map/layer/layer-definitions/T14";
import { CanvasLayer } from "~app/shared/map/layer/layer.model";
import { ILayer } from "~app/shared/map/layer/layer.type";
import { groupBy } from "~app/shared/utils";
import { TagState } from "./measurements/neve-measurements.interface";
import { NeveMeasurementsService } from "./measurements/neve-measurements.service";

@Injectable()
export class NeveMeasurementsMapLayersService {
  constructor(
    private readonly neveMeasurements: NeveMeasurementsService,
    private readonly linkingService: LinkingService,
    private readonly mappingService: MappingService,
  ) {}


  public async setupLinkedMeasurements(canvasLayer: CanvasLayer, layerDefinition: ILayer): Promise<void> {
    // resolve linkings/mappings beforehand, both for performance and to filter out unlinked tags
    const tags: {
      tag: TagPair,
      feature: IRegoFeatureDefinition
    }[] = [];
    for (const tag of layerDefinition.linkedMeasurements) {
      for (const linking of await this.linkingService.getLinkingsForSourceId(tag[0])) {
        const feature = await this.mappingService.getRegoFeatureDefinition(linking.regoId, layerDefinition.id);
        if (feature) {
          tags.push({ tag, feature });
        }
      }
    }

    this.neveMeasurements.init();

    const formatter = layerDefinition.linkedMeasurementFormatter
      ?? ((measurements) => measurements.map(({label, value, unit}) => `${label}: ${value} ${unit}`).join('\n'));

    const properties$: Observable<{
      feature: IRegoFeatureDefinition,
      properties: {
        linkedStatus: 'loading' | 'loaded' | 'partial',
        linkedValues?: string,
        linkedMinValue?: number,
        linkedMaxValue?: number,
        linkedText?: string,
      }
    }[]> = combineLatest(
      tags.map(({ tag, feature }) => this.neveMeasurements.tagState$(tag).pipe(
        // start with a null so that combineLatest doesn't have to wait for all tags to complete loading
        startWith(null as TagState),
        map(state => ({ feature, state })),
      ))
    ).pipe(
      map(entries => {
        return Array.from(groupBy(
          entries,
          entry => entry.feature,
          entry => entry.state,
        ).entries()).map(([feature, states]) => {
          const loadedStates = states.filter(state => state?.status === 'loaded' && state?.measurements?.length > 0);
          if (loadedStates.length === 0) {
            // nothing has loaded yet
            return {
              feature,
              properties: {
                linkedStatus: 'loading' as const,
              }
            };
          }

          const values = loadedStates.map(state => parseFloat(last(state.measurements).value));
          return {
            feature,
            properties: {
              linkedStatus: states.length === loadedStates.length ? ('loaded' as const) : ('partial' as const),
              // arrays aren't allowed, so this is the next best thing...
              // can be used for `linkedValues.includes('|somevalue|')` type queries
              linkedValues: `|${values.join('|')}|`,
              linkedMinValue: Math.min(...values),
              linkedMaxValue: Math.max(...values),
              linkedText: formatter(loadedStates.map(state => ({
                label: state.metadata.tag.Description,
                value: last(state.measurements).value,
                unit: state.metadata.tag.Unit,
              }))),
            }
          };
        });
      }),
      filter(entries => entries.length > 0),
    );

    if (canvasLayer.geojsonURL && canvasLayer.isPointLayer()) {
      properties$.subscribe(entries => {
        canvasLayer.setFeatureProperties(
          Object.fromEntries(
            entries.map(({ feature, properties }) => [feature.id, properties])
          )
        );
      });
    } else {
      properties$.pipe(
        map(entries => {
          return entries.map<Feature>(({ feature, properties }) => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: feature.center,
            },
            id: feature.id,
            properties,
          }));
        }),
      ).subscribe(
        await canvasLayer.addUtilityLayer('linkedMeasurements', {
          style: {
            layerType: 'symbol',
            icon: {
              textField: ['get', 'linkedText'],
            },
          },
          mapboxOptions: {
            layout: {
              'text-radial-offset': 1,
              'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
              'text-max-width': 100, // prevent wrapping
            }
          }
        })
      );
    }
  }

  public async setupLinkedAuxMeasurements(canvasLayer: CanvasLayer, layerDefinition: ILayer): Promise<void> {
    const features: {
      feature: Feature
      tags: ValmetDnaTagConfig[],
    }[] = [];
    for (const tag of layerDefinition.linkedAuxMeasurements) {
      for (const linking of await this.linkingService.getLinkingsForSourceId(tag.tagpair[0])) {
        const baseFeature = await this.mappingService.getRegoFeatureDefinition(linking.regoId, layerDefinition.id);
        if (baseFeature) {
          let feature = features.find(({ feature }) => feature.id === baseFeature.id);
          if (!feature) {
            feature = {
              feature: {
                type: 'Feature',
                id: baseFeature.id,
                geometry: {
                  type: 'Point',
                  coordinates: baseFeature.center,
                },
                properties: {
                  [layerDefinition.metadata.uniqueBy]: baseFeature.id,
                  [layerDefinition.metadata.labelBy]: baseFeature.title ?? baseFeature.id,
                },
              },
              tags: [],
            };
            features.push(feature);
          }
          feature.tags.push(tag);
        }
      }
    }

    const features$ = await canvasLayer.addUtilityLayer('auxMeasurements', {
      style: {
        clickable: true,
        layerType: 'symbol',
        icon: {
          urlExpression: [
            'concat',
            // TODO: configurable image name
            'kpi',
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['==', ['get', 'linkedStatus'], 'partial'], 'loading',
              ['==', ['get', 'linkedOutOfBounds'], true], 'off',
              'on',
            ]
          ]
        },
      },
      mapboxOptions: {
        layout: {
          // TODO: tweak offset
          'icon-offset': [50, 0],
        },
      }
    });

    this.neveMeasurements.init();

    combineLatest(
      features.flatMap(({ tags, feature }) => tags.map(tag => this.neveMeasurements.tagState$(tag.tagpair).pipe(
        // start with a null so that combineLatest doesn't have to wait for all tags to complete loading
        startWith(null as TagState),
        map(state => ({ feature, tag, state })),
      )))
    ).pipe(
      map((entries): Feature[] => {
        return Array.from(groupBy(
          entries,
          ({ feature }) => feature,
          ({ tag, state }) => ({ tag, state }),
        ).entries()).map(([feature, states]): Feature => {
          const loadedStates = states.filter(({ state }) => state?.status === 'loaded' && state?.measurements?.length > 0);
          if (loadedStates.length === 0) {
            // nothing has loaded yet
            return {
              ...feature,
              properties: {
                ...feature.properties,
                linkedStatus: 'loading' as const,
              }
            };
          }

          return {
            ...feature,
            properties: {
              ...feature.properties,
              linkedStatus: states.length === loadedStates.length ? ('loaded' as const) : ('partial' as const),
              linkedOutOfBounds: loadedStates.some(({ state, tag }) => {
                const value = parseFloat(last(state.measurements).value);
                return (tag.minValue !== undefined && value < tag.minValue) || (tag.maxValue !== undefined && value > tag.maxValue);
              }),
              ...Object.fromEntries(loadedStates.map(({ state, tag }) => {
                const value = parseFloat(last(state.measurements).value);
                let text = `${value < 100 ? value.toPrecision(2) : value.toFixed(0)} ${state.metadata.tag.Unit}`;
                if (tag.minValue !== undefined && tag.maxValue !== undefined) {
                  // \u2013 = longer dash
                  text += ` (${tag.minValue} \u2013 ${tag.maxValue} ${state.metadata.tag.Unit})`;
                } else if (tag.minValue !== undefined) {
                  text += ` (> ${tag.minValue} ${state.metadata.tag.Unit})`;
                } else if (tag.maxValue !== undefined) {
                  text += ` (< ${tag.maxValue} ${state.metadata.tag.Unit})`;
                }
                return [state.metadata.tag.Description, text];
              }))
            }
          };
        });
      }),
      filter(features => features.length > 0),
    ).subscribe(features$);
  }
}
